import { getPromotionPrice } from 'helpers/promotionPrice';
/**
 * Highest quantity a customer can pick for an option (same as the server).
 * @const MAX_OPTION_QUANTITY
 * @since 1.0.0
 */
export const MAX_OPTION_QUANTITY = 99;
/**
 * @function compareOptionOrder
 * @since 1.0.0
 */
function compareOptionOrder(a, b) {
    var _a, _b, _c, _d;
    const orderA = (_a = a.order) !== null && _a !== void 0 ? _a : 0;
    const orderB = (_b = b.order) !== null && _b !== void 0 ? _b : 0;
    if (orderA !== orderB) {
        return orderA - orderB;
    }
    return ((_c = a.id) !== null && _c !== void 0 ? _c : '').localeCompare((_d = b.id) !== null && _d !== void 0 ? _d : '');
}
/**
 * @function getIncludedOptions
 * @since 1.0.0
 */
export function getIncludedOptions(saleItem) {
    var _a, _b;
    return ((_b = (_a = saleItem.options) === null || _a === void 0 ? void 0 : _a.filter((option) => option.included)) !== null && _b !== void 0 ? _b : []).sort(compareOptionOrder);
}
/**
 * @function getProposedOptions
 * @since 1.0.0
 */
export function getProposedOptions(saleItem) {
    var _a, _b;
    return ((_b = (_a = saleItem.options) === null || _a === void 0 ? void 0 : _a.filter((option) => option.proposed && !option.included)) !== null && _b !== void 0 ? _b : []).sort(compareOptionOrder);
}
/**
 * @function getPanelOptions
 * @since 1.0.0
 */
export function getPanelOptions(saleItem) {
    return [...getIncludedOptions(saleItem), ...getProposedOptions(saleItem)];
}
/**
 * @function createSelectedItem
 * @since 1.0.0
 */
export function createSelectedItem(saleItem, quantity) {
    return {
        id: saleItem.id,
        quantity,
        selectedOptionIds: []
    };
}
/**
 * @function toggleOptionSelection
 * @since 1.0.0
 */
export function toggleOptionSelection(saleItem, selectedOptionIds, optionItemId, checked) {
    var _a, _b, _c, _d, _e, _f;
    const proposedOptions = getProposedOptions(saleItem);
    const option = proposedOptions.find((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) === optionItemId; });
    if (!((_a = option === null || option === void 0 ? void 0 : option.item) === null || _a === void 0 ? void 0 : _a.id)) {
        return selectedOptionIds;
    }
    let nextIds = [...selectedOptionIds];
    if (checked) {
        if (!nextIds.includes(optionItemId)) {
            nextIds.push(optionItemId);
        }
        // Unique option restriction: one option per restricted group
        for (const group of (_b = option.item.groups) !== null && _b !== void 0 ? _b : []) {
            if (!group.hasUniqueOptionRestriction) {
                continue;
            }
            for (const other of proposedOptions) {
                if (!((_c = other.item) === null || _c === void 0 ? void 0 : _c.id) || other.item.id === optionItemId) {
                    continue;
                }
                const sharesGroup = (_d = other.item.groups) === null || _d === void 0 ? void 0 : _d.some((otherGroup) => otherGroup.id === group.id && otherGroup.hasUniqueOptionRestriction);
                if (sharesGroup) {
                    nextIds = nextIds.filter((id) => id !== other.item.id);
                }
            }
        }
        // INCOMPATIBLE relations: uncheck conflicting selected options
        for (const relation of (_e = option.relations) !== null && _e !== void 0 ? _e : []) {
            if (relation.type !== 'INCOMPATIBLE') {
                continue;
            }
            const relatedId = (_f = relation.relatedSaleItem) === null || _f === void 0 ? void 0 : _f.id;
            if (relatedId && nextIds.includes(relatedId)) {
                nextIds = nextIds.filter((id) => id !== relatedId);
            }
        }
    }
    else {
        nextIds = nextIds.filter((id) => id !== optionItemId);
    }
    return nextIds;
}
/**
 * Quantity of an option for the whole line. An option the customer picks the
 * quantity of starts at the catalog quantity, not multiplied by the product
 * quantity; the others take the catalog quantity for each product.
 * @function getOptionQuantity
 * @since 1.0.0
 */
export function getOptionQuantity(option, productQuantity, optionQuantities) {
    var _a, _b;
    if (!option.quantityEditable || !((_a = option.item) === null || _a === void 0 ? void 0 : _a.id)) {
        return option.quantity * productQuantity;
    }
    return (_b = optionQuantities === null || optionQuantities === void 0 ? void 0 : optionQuantities[option.item.id]) !== null && _b !== void 0 ? _b : option.quantity;
}
/**
 * @function getSelectedOptionQuantity
 * @since 1.0.0
 */
export function getSelectedOptionQuantity(selectedItem, option) {
    return getOptionQuantity(option, selectedItem.quantity, selectedItem.optionQuantities);
}
/**
 * Adds or removes a proposed option on a selected product. An option whose
 * quantity the customer picks starts at the catalog quantity, then keeps the
 * quantity picked: the product quantity never changes it.
 * @function toggleSelectedOption
 * @since 1.0.0
 */
export function toggleSelectedOption(saleItem, selectedItem, optionItemId, checked) {
    var _a;
    const selectedOptionIds = toggleOptionSelection(saleItem, selectedItem.selectedOptionIds, optionItemId, checked);
    const optionQuantities = {};
    for (const option of getProposedOptions(saleItem)) {
        const id = (_a = option.item) === null || _a === void 0 ? void 0 : _a.id;
        if (!id || !option.quantityEditable || !selectedOptionIds.includes(id)) {
            continue;
        }
        optionQuantities[id] = getSelectedOptionQuantity(selectedItem, option);
    }
    return Object.assign(Object.assign({}, selectedItem), { selectedOptionIds, optionQuantities });
}
/**
 * @function setOptionQuantity
 * @since 1.0.0
 */
export function setOptionQuantity(selectedItem, optionItemId, quantity) {
    return Object.assign(Object.assign({}, selectedItem), { optionQuantities: Object.assign(Object.assign({}, selectedItem.optionQuantities), { [optionItemId]: Math.min(MAX_OPTION_QUANTITY, Math.max(1, Math.round(quantity))) }) });
}
/**
 * @function buildOptionsPayload
 * @since 1.0.0
 */
export function buildOptionsPayload(selectedItems, saleItems) {
    return selectedItems.map((selectedItem) => {
        const saleItem = saleItems.find((entry) => entry.id === selectedItem.id);
        const proposedOptions = saleItem ? getProposedOptions(saleItem) : [];
        const options = selectedItem.selectedOptionIds
            .map((optionItemId) => {
            var _a;
            const option = proposedOptions.find((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) === optionItemId; });
            if (!((_a = option === null || option === void 0 ? void 0 : option.item) === null || _a === void 0 ? void 0 : _a.id)) {
                return null;
            }
            return {
                itemId: option.item.id,
                quantity: getSelectedOptionQuantity(selectedItem, option),
                price: option.price
            };
        })
            .filter(Boolean);
        return {
            itemId: selectedItem.id,
            options
        };
    });
}
/**
 * @function getProductPrice
 * @since 1.0.0
 */
export function getProductPrice(saleItem) {
    var _a, _b;
    return ((_b = (_a = getPromotionPrice(saleItem.price, saleItem.promotions)) !== null && _a !== void 0 ? _a : saleItem.price) !== null && _b !== void 0 ? _b : 0);
}
/**
 * @function calculateSelectionTotal
 * @since 1.0.0
 */
export function calculateSelectionTotal(selectedItems, saleItems, extrasOnly = false) {
    return selectedItems.reduce((total, selectedItem) => {
        const saleItem = saleItems.find((entry) => entry.id === selectedItem.id);
        if (!saleItem) {
            return total;
        }
        let itemTotal = extrasOnly
            ? 0
            : getProductPrice(saleItem) * selectedItem.quantity;
        if (!extrasOnly) {
            const includedOptions = getIncludedOptions(saleItem);
            for (const option of includedOptions) {
                itemTotal += option.price * option.quantity * selectedItem.quantity;
            }
        }
        const proposedOptions = getProposedOptions(saleItem);
        for (const optionItemId of selectedItem.selectedOptionIds) {
            const option = proposedOptions.find((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) === optionItemId; });
            if (option) {
                itemTotal +=
                    option.price * getSelectedOptionQuantity(selectedItem, option);
            }
        }
        return total + itemTotal;
    }, 0);
}
export function dealHasUpsellOptions(deal) {
    var _a, _b;
    return ((_b = (_a = deal === null || deal === void 0 ? void 0 : deal.items) === null || _a === void 0 ? void 0 : _a.some((item) => { var _a; return (_a = item.options) === null || _a === void 0 ? void 0 : _a.some((option) => option.proposed && !option.included); })) !== null && _b !== void 0 ? _b : false);
}
export function buildStoreEnhancePath(lang, dealId) {
    return `/store/${lang}/enhance/${dealId}`;
}
/**
 * @function getOptionTitle
 * @since 1.0.0
 */
export function getOptionTitle(option, lang) {
    var _a, _b, _c, _d, _e;
    const translation = (_b = (_a = option.item) === null || _a === void 0 ? void 0 : _a.translations) === null || _b === void 0 ? void 0 : _b.find((entry) => entry.language.code === lang);
    return (_e = (_c = translation === null || translation === void 0 ? void 0 : translation.title) !== null && _c !== void 0 ? _c : (_d = option.item) === null || _d === void 0 ? void 0 : _d.title) !== null && _e !== void 0 ? _e : '';
}
/**
 * @function getProductTitle
 * @since 1.0.0
 */
export function getProductTitle(saleItem, lang) {
    var _a, _b, _c;
    const translation = (_a = saleItem.translations) === null || _a === void 0 ? void 0 : _a.find((entry) => entry.language.code === lang);
    return (_c = (_b = translation === null || translation === void 0 ? void 0 : translation.title) !== null && _b !== void 0 ? _b : saleItem.title) !== null && _c !== void 0 ? _c : '';
}
